import React, { useState, useEffect } from 'react'
import MemoryTile from '@fs/zion-memory-tile'
import { useTranslation } from 'react-i18next'
import axios from '@fs/zion-axios'
import { i18n } from '@fs/zion-locale'
import { getNameRelatedData } from '@fs/zion-person-service'
import {
  colors,
  Avatar,
  Ellipsis,
  Card,
  Bleed,
  Paragraph,
  Tag,
  Divider,
  MarginBox,
  Skeleton,
  Separator,
} from '@fs/zion-ui'
import { css } from '@linaria/core'
import ClickToCopy from '@fs/zion-click-to-copy'
import type { NameForm, TfData, TfMemory, TfPerson } from '@fs/zion-tree-types'
import AncestorEvent from './AncestorEvent'
import AncestorRelative from './AncestorRelative'

const flexCss = css`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: space-between;
  height: 226px;
`

const lowerCardCss = css`
  background-color: ${colors.gray02};
`

const noMemoriesCss = css`
  height: 64px;
  display: flex;
  align-items: center;
`

const cardWrapperCss = css`
  width: 300px;
`

const tagCss = css`
  inset-inline-start: 12px;
  top: 12px;
  position: absolute;
  text-transform: uppercase;
`

const memoriesCss = css`
  align-items: center;
  display: flex;
  column-gap: 4px;
  & > div {
    width: 64px;
    min-width: 64px;
    height: 64px;
    padding: 0;
  }
`

const personContainerCss = css`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const avatarContainerCss = css`
  margin: 0 auto 8px auto;
`

const chipCorrectionCss = css`
  & button {
    inset-inline-start: 0;
  }
`

export function getThumbnailUrl(memory: TfMemory): string | undefined {
  let thumbnailUrl = memory.thumbnailUrl
  if (!thumbnailUrl) {
    thumbnailUrl = memory?.associatedMedia?.find((artifact) => {
      return artifact.thumbnailUrl
    })?.thumbnailUrl
  }
  return thumbnailUrl
}

export function getMemoryTileType(memory: TfMemory): string {
  let tileType = memory.memoryType
  if (memory.memoryType === 'PHOTO') {
    tileType = memory?.associatedMedia?.find((artifact) => {
      return artifact.memoryType === 'AUDIO'
    })
      ? 'AUDIO'
      : 'PHOTO'
  }
  return tileType ? tileType.toLowerCase() : 'photo'
}

export function getTreeDataPerson(person: TfPerson): {
  name: string | undefined
  nameConclusion: { details: { fullText: string | undefined; nameForms: [NameForm]; style: string | undefined } }
} {
  const nameForm = person.nameForms?.[0]
  const nameFormsObject = {
    familyPart: nameForm?.parts?.find((name) => name.type.includes('Surname'))?.value,
    fullText: nameForm?.fullText,
    givenPart: nameForm?.parts?.find((name) => name.type.includes('Given'))?.value,
    lang: nameForm?.lang,
    prefixPart: nameForm?.parts?.find((name) => name.type.includes('Prefix'))?.value,
    suffixPart: nameForm?.parts?.find((name) => name.type.includes('Suffix'))?.value,
  }
  return {
    name: person?.name,
    nameConclusion: {
      details: {
        fullText: nameForm?.fullText,
        nameForms: [nameFormsObject],
        style: nameForm?.order,
      },
    },
  }
}

type Props = {
  ancestorData: Pick<TfData, 'person' | 'localizedData'>
  hidePid?: boolean
  tag?: string
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'div'
  personId?: string
}

export default function AncestorCard({
  ancestorData,
  personId,
  hidePid,
  tag,
  as: HeadingComp = 'div',
}: Props): JSX.Element {
  const [t] = useTranslation()
  const [personData, setPersonData] = useState(ancestorData)

  useEffect(() => {
    let mounted = true
    if (personId) {
      const endpointUrl = `/service/tree/tree-data/published/persons/${personId}`
      axios.get(endpointUrl)?.then((response) => {
        mounted && setPersonData(response.data)
      })
    }
    return () => {
      mounted = false
    }
  }, [personId])

  const { person, localizedData } = personData
  const personObj = getTreeDataPerson(person)
  const nameRelatedData = getNameRelatedData(personObj)

  const memories = person.memoryArtifacts
  const viewAllMemory = memories?.[3]
  const displayedMemories = viewAllMemory ? memories?.slice(0, 3) : memories?.slice(0, 4)
  const noMemories = person.memories === 0

  const age = person.ageAtDeath
  const ageString = age === undefined || age === 0 ? '' : t('ancestor-card.age.text', { age })
  const lifespan = (person.lifespan && localizedData[i18n.language]?.[person.lifespan]) ?? ''
  return (
    <div className={cardWrapperCss}>
      <HeadingComp data-testid="ancestor-card-heading">
        <Card>
          <Bleed all>
            {tag && (
              <div className={tagCss}>
                <Tag color="blue" text={tag} />
              </div>
            )}

            <Separator />

            <MarginBox marginY="none" marginX="xxs">
              <div className={personContainerCss}>
                <div className={avatarContainerCss}>
                  <Avatar
                    size="lg"
                    src={person?.portrait?.url}
                    sex={person.gender.toLowerCase() as Lowercase<(typeof person)['gender']>}
                  />
                </div>
                <Paragraph centered>
                  <Ellipsis>{nameRelatedData.namePart1}</Ellipsis>
                </Paragraph>
                <div />
                <Paragraph centered size="lg">
                  <Ellipsis>{nameRelatedData.namePart2}</Ellipsis>
                </Paragraph>
                <div />
                <Paragraph centered size="sm">
                  <Ellipsis>{`${lifespan} ${ageString}`}</Ellipsis>
                </Paragraph>
                {person.identifier && !hidePid && (
                  <Bleed top>
                    <div className={chipCorrectionCss}>
                      <Paragraph centered size="sm">
                        <ClickToCopy value={person.identifier} inline />
                      </Paragraph>
                    </div>
                  </Bleed>
                )}
              </div>
            </MarginBox>

            <Separator size="xxs" />

            <div className={lowerCardCss}>
              <Divider />
              <MarginBox marginY="xxs" marginX="xxs">
                <div className={flexCss}>
                  <AncestorEvent ancestorData={ancestorData} />
                  <AncestorRelative ancestorData={ancestorData} />
                  {noMemories ? (
                    <div className={noMemoriesCss}>
                      <Paragraph size="xs">{t('ancestor-card.no-memories.label')}</Paragraph>
                    </div>
                  ) : (
                    <div className={memoriesCss}>
                      {displayedMemories?.map((memory, index) => {
                        const key = `memory - ${index}`
                        return (
                          <MemoryTile
                            key={key}
                            type={getMemoryTileType(memory)}
                            image={getThumbnailUrl(memory)}
                            data-testid="memory-tile"
                          />
                        )
                      })}
                      {viewAllMemory && (
                        <MemoryTile
                          type={getMemoryTileType(viewAllMemory)}
                          image={getThumbnailUrl(viewAllMemory)}
                          viewAllCount={memories.length - 3}
                          data-testid="memory-tile-view-all"
                        />
                      )}
                    </div>
                  )}
                </div>
              </MarginBox>
            </div>
          </Bleed>
        </Card>
      </HeadingComp>
    </div>
  )
}

const separatorCss = css`
  height: 23px;
`

const lifespanCss = css`
  margin-top: -5px;
`

type SkeletonProps = {
  hidePid?: boolean
}

export function SkeletonAncestorCard({ hidePid }: SkeletonProps): JSX.Element {
  return (
    <div className={cardWrapperCss} data-testid="skeleton-card">
      <Card>
        <Bleed all>
          <Separator />

          <MarginBox marginY="none" marginX="xxs">
            <div className={personContainerCss}>
              <div className={avatarContainerCss}>
                <Skeleton.Avatar size="lg" centered />
              </div>
              <Skeleton.Paragraph centered maxWidth={125} />
              <div />
              <Skeleton.Paragraph size="lg" centered maxWidth={280} />
              <div className={lifespanCss}>
                <Skeleton.Paragraph centered maxWidth={180} />
              </div>
            </div>
          </MarginBox>

          {!hidePid && <div className={separatorCss} />}

          <Separator size="xxs" />

          <div className={lowerCardCss}>
            <Divider />
            <MarginBox marginY="xxs" marginX="xxs">
              <div className={flexCss}>
                <div>
                  <Skeleton.Paragraph maxWidth={60} />
                  <div />
                  <Skeleton.Paragraph size="xs" maxWidth={200} />
                </div>

                <div>
                  <Skeleton.Paragraph maxWidth={100} />
                  <Skeleton.PersonBlock avatarSize="smaller" />
                </div>

                <div className={memoriesCss}>
                  <Skeleton.Image height="64px" width="64px" />
                  <Skeleton.Image height="64px" width="64px" />
                  <Skeleton.Image height="64px" width="64px" />
                  <Skeleton.Image height="64px" width="64px" />
                </div>
              </div>
            </MarginBox>
          </div>
        </Bleed>
      </Card>
    </div>
  )
}
